import { useState, useEffect, useRef } from 'react'
import { 
  User, 
  Award, 
  Calendar, 
  MessageSquare,
  Star,
  Settings,
  LogOut,
  Loader2,
  X,
  Camera,
  Edit3
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { goToLogin } from '../utils/authRedirect'
import api from '../utils/api'

function Profile() {
  const { user, logout, getLevelName, fetchUser } = useAuth()
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showSettings, setShowSettings] = useState(false)
  const [editing, setEditing] = useState(false)
  const [formData, setFormData] = useState({
    nickname: '',
    avatar: ''
  })
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' })
  const [previewAvatar, setPreviewAvatar] = useState(null)
  const fileInputRef = useRef(null)

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type })
    setTimeout(() => setToast({ show: false, message: '', type }), 3000)
  }

  useEffect(() => {
    if (user) {
      fetchUserStats()
    }
  }, [user])

  const fetchUserStats = async () => {
    try {
      const response = await api.get('/user/stats')
      if (response.data.success) {
        setStats(response.data.stats)
      }
    } catch (error) {
      console.error('获取用户统计失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatJoinTime = (value) => {
    if (!value) return '-'
    const normalized = typeof value === 'string' ? value.replace(' ', 'T') : value
    const d = new Date(normalized)
    if (Number.isNaN(d.getTime())) return '-'
    return d.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const handleSettingsClick = () => {
    setFormData({
      nickname: user?.nickname || ''
    })
    setPreviewAvatar(null)
    setShowSettings(true)
    setEditing(false)
  }

  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      // 预览
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreviewAvatar(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSave = async () => {
    if (!formData.nickname.trim()) {
      showToast('昵称不能为空', 'error')
      return
    }
    
    setSaving(true)
    try {
      // 使用 FormData 上传文件
      const submitData = new FormData()
      submitData.append('nickname', formData.nickname)
      
      // 如果有选择文件，添加到 FormData
      if (fileInputRef.current?.files?.[0]) {
        submitData.append('avatar', fileInputRef.current.files[0])
      }
      
      const response = await api.put('/user/profile', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      if (response.data.success) {
        await fetchUser()
        setEditing(false)
        // 清空文件输入
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        showToast('保存成功')
      }
    } catch (error) {
      showToast(error.response?.data?.error || '保存失败', 'error')
    } finally {
      setSaving(false)
    }
  }

  if (!user) {
    return (
      <div className="text-center py-12 px-4">
        <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base">请先登录</p>
        <button
          onClick={() => goToLogin('/login')}
          className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-dclaw-600 hover:bg-dclaw-700 text-white rounded-xl font-medium transition-colors mt-4 text-sm sm:text-base min-h-[44px]"
        >
          立即登录
        </button>
      </div>
    )
  }

  return (
    <div className="animate-in">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Profile Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-5 sm:p-6 lg:p-8 border border-gray-200 dark:border-gray-700 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
            {/* Avatar */}
            <div className="relative">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.nickname}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover"
                />
              ) : (
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-dclaw-400 to-dclaw-600 flex items-center justify-center text-white text-2xl sm:text-3xl font-bold">
                  {user?.nickname?.[0] || user?.phone?.slice(-1) || '?'}
                </div>
              )}
              <div className="absolute -bottom-1.5 -right-1.5 sm:-bottom-2 sm:-right-2 w-7 h-7 sm:w-8 sm:h-8 bg-dclaw-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                {user.level}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 text-center sm:text-left">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                {user.nickname}
              </h1>
              <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 mb-3 sm:mb-4">
                {user?.phone?.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') || '未绑定手机号'}
              </p>
              
              {/* Level Badge */}
              <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-dclaw-100 dark:bg-dclaw-900/30 text-dclaw-700 dark:text-dclaw-300 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Lv{user.level} {getLevelName(user.level)}
              </div>
            </div>

            {/* Actions */}
            <div className="flex sm:flex-col gap-2 w-full sm:w-auto">
              <button 
                onClick={handleSettingsClick}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-xs sm:text-sm font-medium transition-colors min-h-[44px]"
              >
                <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                设置
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700 text-center">
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1">
              {stats?.postCount || 0}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">帖子</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700 text-center">
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1">
              {stats?.replyCount || 0}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">回复</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700 text-center">
            <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-1">
              {stats?.likeCount || 0}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">获赞</p>
          </div>
        </div>

        {/* Join Date */}
        <div className="bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 sm:gap-3 text-gray-600 dark:text-gray-400 text-sm sm:text-base">
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>
              加入时间：{formatJoinTime(user.created_at)}
            </span>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                个人设置
              </h2>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Avatar */}
              <div className="flex flex-col items-center">
                <div 
                  onClick={editing ? handleAvatarClick : undefined}
                  className={`relative w-24 h-24 rounded-full overflow-hidden ${editing ? 'cursor-pointer hover:ring-4 hover:ring-dclaw-500/30' : ''}`}
                >
                  {previewAvatar ? (
                    <img src={previewAvatar} alt="avatar" className="w-full h-full object-cover" />
                  ) : user?.avatar ? (
                    <img src={user.avatar} alt="avatar" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-dclaw-400 to-dclaw-600 flex items-center justify-center text-white text-2xl font-bold">
                      {user?.nickname?.[0] || user?.phone?.slice(-1) || '?'}
                    </div>
                  )}
                  {editing && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <Camera className="w-8 h-8 text-white" />
                    </div>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {editing && <p className="text-sm text-gray-500 mt-2">点击更换头像</p>}
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                {/* Nickname */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    昵称
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={formData.nickname}
                      onChange={(e) => setFormData(prev => ({ ...prev, nickname: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-dclaw-500 focus:border-transparent"
                      placeholder="请输入昵称"
                    />
                  ) : (
                    <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <span className="text-gray-900 dark:text-white">{user?.nickname}</span>
                      <button
                        onClick={() => setEditing(true)}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                      >
                        <Edit3 className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Phone (Read Only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    手机号
                  </label>
                  <div className="px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-500">
                    {user?.phone?.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') || '未绑定'}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">手机号不可修改</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              {editing ? (
                <>
                  <button
                    onClick={() => {
                      setEditing(false)
                      setFormData({
                        nickname: user?.nickname || '',
                        avatar: user?.avatar || ''
                      })
                    }}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-dclaw-600 hover:bg-dclaw-700 disabled:opacity-50 text-white rounded-lg transition-colors"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    保存
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                >
                  关闭
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg z-50 animate-in fade-in slide-in-from-bottom-2 ${
          toast.type === 'success' 
            ? 'bg-green-600 text-white' 
            : 'bg-red-600 text-white'
        }`}>
          <div className="flex items-center gap-2">
            {toast.type === 'success' ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default Profile
